import React from "react";
import {
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  motion,
  useReducedMotion,
} from "framer-motion";

import LanguageSwitcher from "../../../components/LanguageSwitcher";
import LandingFooter from "../../../components/layout/LandingFooter";
import LandingMenu from "../../../components/layout/LandingMenu";
import LoginButton from "../../../components/layout/LoginButton";

import SimulationCards from "./components/SimulationCards";

import LoanSimulatorBelgium from "./loan-simulator/LoanSimulatorBelgium";
import LoanSimulatorGirona from "./loan-simulator/LoanSimulatorGirona";
import InstallmentsCalculatorFlanders from "./monthly-installments-calculator/InstallmentsCalculatorFlanders";
import InstallmentsCalculatorGirona from "./monthly-installments-calculator/InstallmentsCalculatorGirona";
import RenCostEstimatorFlanders from "./renovation-cost-estimator/RenCostEstimatorFlanders";
import RenCostEstimatorGirona from "./renovation-cost-estimator/RenCostEstimatorGirona";

import logoImg from "../../../assets/logo2.png";
import "./SimulationPage.css";

const SIMULATORS_BY_REGION = {
  flanders: {
    loan: LoanSimulatorBelgium,
    installment:
      InstallmentsCalculatorFlanders,
    renCost:
      RenCostEstimatorFlanders,
  },

  girona: {
    loan: LoanSimulatorGirona,
    installment:
      InstallmentsCalculatorGirona,
    renCost:
      RenCostEstimatorGirona,
  },
};

const SIMULATION_ORDER = [
  "loan",
  "installment",
  "renCost",
];

const EASE = [0.22, 1, 0.36, 1];

function normaliseRegion(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export default function SimulationPage() {
  const navigate = useNavigate();

  const [searchParams] =
    useSearchParams();

  const region = normaliseRegion(
    searchParams.get("region"),
  );

  const simulators =
    SIMULATORS_BY_REGION[region];

  const sectionRefs =
    React.useRef({});

  const prefersReducedMotion =
    useReducedMotion();

  React.useEffect(() => {
    if (simulators) {
      return;
    }

    navigate("/services", {
      replace: true,
    });
  }, [navigate, simulators]);

  React.useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [region]);

  const revealVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion
        ? 0
        : 42,
    },

    visible: {
      opacity: 1,
      y: 0,

      transition: {
        duration:
          prefersReducedMotion
            ? 0.01
            : 0.68,
        ease: EASE,
      },
    },
  };

  const scrollToSimulation = (
    id,
  ) => {
    const section =
      sectionRefs.current[id];

    if (!section) {
      return;
    }

    section.scrollIntoView({
      behavior:
        prefersReducedMotion
          ? "auto"
          : "smooth",
      block: "start",
    });
  };

  const handleBack = (id) => {
    const currentIndex =
      SIMULATION_ORDER.indexOf(id);

    if (currentIndex <= 0) {
      navigate("/services", {
        state: {
          step: 2,
          region,
        },
      });

      return;
    }

    scrollToSimulation(
      SIMULATION_ORDER[
        currentIndex - 1
      ],
    );
  };

  const handleContinue = (id) => {
    const currentIndex =
      SIMULATION_ORDER.indexOf(id);

    const nextId =
      SIMULATION_ORDER[
        currentIndex + 1
      ];

    if (nextId) {
      scrollToSimulation(nextId);
      return;
    }

    navigate("/register/homebuyer", {
      state: {
        source: "simulations",
        region,
      },
    });
  };

  if (!simulators) {
    return null;
  }

  return (
    <div>
      <div className="simulation-page">
        <div className="select-service-topbar">
          <LandingMenu />

          <Link
            to="/"
            className="select-service-logo"
            aria-label="LEG-UP home"
          >
            <img
              src={logoImg}
              alt="LEG-UP"
            />
          </Link>

          <div className="select-service-topbar__right">
            <LanguageSwitcher />
            <LoginButton />
          </div>
        </div>

        <main>
          <SimulationCards
            onSelect={
              scrollToSimulation
            }
          />

          {SIMULATION_ORDER.map(
            (id) => {
              const Simulator =
                simulators[id];

              if (!Simulator) {
                return null;
              }

              return (
                <section
                  key={id}
                  id={`simulation-${id}`}
                  ref={(element) => {
                    sectionRefs.current[
                      id
                    ] = element;
                  }}
                  className="simulation-page__section"
                >
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{
                      once: true,
                      amount: 0.08,
                    }}
                    variants={
                      revealVariants
                    }
                  >
                    <Simulator
                      onBack={() =>
                        handleBack(id)
                      }
                      onContinue={() =>
                        handleContinue(id)
                      }
                    />
                  </motion.div>
                </section>
              );
            },
          )}
        </main>
      </div>

      <LandingFooter />
    </div>
  );
}
